import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { Wrench } from "lucide-react";

/**
 * If maintenance_mode is "on" AND the current viewer is not an admin
 * AND the route is not /login (so admin can still sign in), show the
 * maintenance page. The backend enforces the same gate at the API level,
 * with an additional IP-whitelist bypass for visitors the admin trusts.
 */
export default function MaintenanceGate({ children }) {
  const { settings, loaded } = useSettings();
  const { user, loading } = useAuth();
  const location = useLocation();

  const maintOn = String(settings.maintenance_mode || "off").toLowerCase() === "on";
  const isAdmin = user && user !== false && user.role === "admin";

  // Always allow login + auth callback so admins can sign back in
  const bypassPath =
    location.pathname.startsWith("/login") ||
    location.pathname.startsWith("/auth/callback");

  // While settings/auth are still loading, render children to avoid flashes
  if (!loaded || loading) return children;

  if (!maintOn || isAdmin || bypassPath) return children;

  return (
    <div
      data-testid="maintenance-page"
      className="min-h-screen bg-secondary flex items-center justify-center px-6 py-12"
    >
      <div className="max-w-xl w-full text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] mb-6">
          <Wrench className="w-6 h-6" />
        </div>
        <div className="overline mb-3">{settings.site_name || "PeptideSeries"}</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-tight">
          {settings.maintenance_title || "We'll be right back"}
        </h1>
        <p className="mt-6 text-sm sm:text-base text-muted-foreground max-w-md mx-auto leading-relaxed">
          {settings.maintenance_message ||
            "We are performing scheduled maintenance. Please check back shortly."}
        </p>
        <a
          href="/login"
          data-testid="maintenance-admin-signin"
          className="mt-8 inline-block text-xs font-mono uppercase tracking-widest underline text-muted-foreground hover:text-foreground"
        >
          Administrator sign-in →
        </a>
      </div>
    </div>
  );
}
